import { useState } from "react";
import axios from "axios";
import {
    Mail,
    Phone,
    User,
    MessageSquare,
    FileText,
    ArrowRight,
    ShieldCheck,
    CheckCircle2,
    AlertCircle,
} from "lucide-react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

export default function ContactForm() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
    });

    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const { executeRecaptcha } = useGoogleReCaptcha();

    const handleChange = (e) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value,
        });

        setError("");
        setSuccess("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (
            !form.name.trim() ||
            !form.email.trim() ||
            !form.phone.trim() ||
            !form.message.trim()
        ) {
            setError("Please fill all required fields.");
            return;
        }

        setLoading(true);
        setError("");
        setSuccess("");

        try {
            if (!executeRecaptcha) {
                setError(
                    "reCAPTCHA is not loaded. Please refresh the page and try again."
                );
                setLoading(false);
                return;
            }

            const token = await executeRecaptcha("contact_form");

            const res = await axios.post(
                "https://dubaihotelrates.com/api/contact",
                {
                    ...form,
                    recaptcha_token: token,
                }
            );

            if (res.data.success) {
                setSuccess(
                    "Your message has been sent successfully. Our team will get back to you shortly."
                );

                setForm({
                    name: "",
                    email: "",
                    phone: "",
                    subject: "",
                    message: "",
                });
            } else {
                setError(
                    res.data.message || "Failed to send your message."
                );
            }
        } catch (err) {
            setError(
                err.response?.data?.message ||
                "Something went wrong. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const inputClass =
        "w-full rounded-xl border border-slate-200 bg-[#F7FAFE] py-3.5 pl-11 pr-4 text-sm text-[#0F172A] placeholder:text-slate-400 outline-none transition-all duration-200 focus:border-[#174EA6] focus:bg-white focus:ring-4 focus:ring-[#174EA6]/10";

    return (
        <div className="w-full">

            {/* =====================================================
            FORM CARD
        ===================================================== */}

            <div className="relative overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_12px_40px_rgba(15,23,42,0.06)]">

                {/* Top Accent */}
                <div className="h-1 w-full bg-gradient-to-r from-[#174EA6] via-[#174EA6] to-[#FFC928]" />

                <div className="p-6 sm:p-8 lg:p-10">

                    {/* =================================================
                    FORM HEADER
                ================================================= */}

                    <div className="mb-7">

                        <div className="inline-flex items-center gap-2 rounded-full bg-[#EAF3FF] px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#174EA6]">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#FFC928]" />
                            Send An Enquiry
                        </div>

                        <h2 className="mt-3 text-2xl font-extrabold leading-tight text-[#0F172A] sm:text-3xl">
                            How Can We Help?
                        </h2>

                        <p className="mt-2 max-w-xl text-sm leading-6 text-slate-500">
                            Fill in the form below and our team will get back to
                            you as soon as possible.
                        </p>

                    </div>


                    {/* =================================================
                    SUCCESS MESSAGE
                ================================================= */}

                    {success && (
                        <div className="mb-6 flex items-start gap-3 rounded-2xl border border-green-200 bg-green-50 p-4">

                            <CheckCircle2
                                size={20}
                                className="mt-0.5 shrink-0 text-green-600"
                            />

                            <div>
                                <p className="text-sm font-bold text-green-700">
                                    Message Sent Successfully
                                </p>

                                <p className="mt-1 text-xs leading-5 text-green-600">
                                    {success}
                                </p>
                            </div>

                        </div>
                    )}


                    {/* =================================================
                    ERROR MESSAGE
                ================================================= */}

                    {error && (
                        <div className="mb-6 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4">

                            <AlertCircle
                                size={20}
                                className="mt-0.5 shrink-0 text-red-600"
                            />

                            <div>
                                <p className="text-sm font-bold text-red-700">
                                    Unable to Send Message
                                </p>

                                <p className="mt-1 text-xs leading-5 text-red-600">
                                    {error}
                                </p>
                            </div>

                        </div>
                    )}


                    {/* =================================================
                    FORM
                ================================================= */}

                    <form
                        onSubmit={handleSubmit}
                        className="space-y-5"
                    >

                        {/* Name + Email */}

                        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">

                            {/* Name */}

                            <div>
                                <label className="mb-2 block text-xs font-bold text-[#0F172A]">
                                    Your Name
                                    <span className="ml-1 text-red-500">*</span>
                                </label>

                                <div className="relative">

                                    <User
                                        size={17}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                                    />

                                    <input
                                        type="text"
                                        name="name"
                                        value={form.name}
                                        onChange={handleChange}
                                        placeholder="Enter your name"
                                        className={inputClass}
                                    />

                                </div>
                            </div>


                            {/* Email */}

                            <div>
                                <label className="mb-2 block text-xs font-bold text-[#0F172A]">
                                    Email Address
                                    <span className="ml-1 text-red-500">*</span>
                                </label>

                                <div className="relative">

                                    <Mail
                                        size={17}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                                    />

                                    <input
                                        type="email"
                                        name="email"
                                        value={form.email}
                                        onChange={handleChange}
                                        placeholder="info@southtravels.com"
                                        className={inputClass}
                                    />

                                </div>
                            </div>

                        </div>


                        {/* Phone + Subject */}

                        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">

                            {/* Phone */}

                            <div>
                                <label className="mb-2 block text-xs font-bold text-[#0F172A]">
                                    Phone Number
                                    <span className="ml-1 text-red-500">*</span>
                                </label>

                                <div className="relative">

                                    <Phone
                                        size={17}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                                    />

                                    <input
                                        type="tel"
                                        name="phone"
                                        value={form.phone}
                                        onChange={handleChange}
                                        placeholder="+971 4 447 3839"
                                        className={inputClass}
                                    />

                                </div>
                            </div>


                            {/* Subject */}

                            <div>
                                <label className="mb-2 block text-xs font-bold text-[#0F172A]">
                                    Subject
                                </label>

                                <div className="relative">

                                    <FileText
                                        size={17}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                                    />

                                    <input
                                        type="text"
                                        name="subject"
                                        value={form.subject}
                                        onChange={handleChange}
                                        placeholder="How can we help?"
                                        className={inputClass}
                                    />

                                </div>
                            </div>

                        </div>


                        {/* Message */}

                        <div>

                            <label className="mb-2 block text-xs font-bold text-[#0F172A]">
                                Your Message
                                <span className="ml-1 text-red-500">*</span>
                            </label>

                            <div className="relative">

                                <MessageSquare
                                    size={17}
                                    className="absolute left-4 top-4 text-slate-400"
                                />

                                <textarea
                                    name="message"
                                    rows="6"
                                    value={form.message}
                                    onChange={handleChange}
                                    placeholder="Tell us how we can help..."
                                    className={`${inputClass} resize-none py-3.5`}
                                />

                            </div>

                        </div>


                        {/* =================================================
                        SUBMIT AREA
                    ================================================= */}

                        <div className="flex flex-col gap-4 border-t border-slate-100 pt-5 sm:flex-row sm:items-center sm:justify-between">

                            {/* Security */}

                            <div className="flex items-center gap-2.5">

                                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#EAF3FF]">

                                    <ShieldCheck
                                        size={15}
                                        className="text-[#174EA6]"
                                    />

                                </div>

                                <p className="text-[11px] leading-4 text-slate-400">
                                    Your information is secure.
                                    <br />
                                    We usually respond within 24 hours.
                                </p>

                            </div>


                            {/* Submit */}

                            <button
                                type="submit"
                                disabled={loading}
                                className="
                                group
                                flex
                                min-h-[50px]
                                w-full
                                items-center
                                justify-center
                                gap-3
                                rounded-xl
                                bg-gradient-to-r
                                from-[#174EA6]
                                via-[#174EA6]
                                to-[#FFC928]
                                px-7
                                py-3.5
                                text-sm
                                font-extrabold
                                text-white
                                shadow-[0_10px_25px_rgba(23,78,166,0.18)]
                                transition-all
                                duration-300
                                hover:-translate-y-0.5
                                hover:shadow-[0_15px_35px_rgba(23,78,166,0.25)]
                                disabled:cursor-not-allowed
                                disabled:opacity-60
                                sm:w-auto
                            "
                            >

                                {loading ? (
                                    <>
                                        <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />

                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        Send Message

                                        <ArrowRight
                                            size={18}
                                            className="transition-transform duration-300 group-hover:translate-x-1"
                                        />
                                    </>
                                )}

                            </button>

                        </div>

                    </form>

                </div>

            </div>

        </div>
    );
}